import { useEffect, useRef } from 'react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { GlobalState } from '@highlight/ipc'
import { CircleCheck, ListChecks } from 'lucide-react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useActiveChatId } from '@renderer/chat/store'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { getSidepanelTopView } from '@renderer/overlay/Sidepanel/view-atoms'
import { viewContextOf } from '@renderer/overlay/Sidepanel/view-definitions'
import type { ReminderApiItem } from '@renderer/overlay/Sidepanel/hooks/use-reminders'
import { ReminderToast } from '../toasts/reminder-toast'
import { AutoResolvedReminderToast } from '../toasts/auto-resolved-reminder-toast'
import { toastManager } from '../toast-store'
import { useToast } from '../use-toast'
import { isOnboardingComplete } from '@shared/onboarding/onboarding-flags'

type ToastApi = ReturnType<typeof useToast>

export function useReminderToasts(toast: ToastApi) {
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const reminderBatchRef = useRef<Array<ReminderApiItem>>([])
  const pendingReminderAnalyticsRef = useRef(false)

  // Track live activeChatId via ref so the event callback (not a hook) can
  // read it without stale closure issues. Mirrors use-action-toasts.tsx.
  const liveActiveChatIdRef = useRef<string | undefined>(undefined)
  const liveActiveChatId = useActiveChatId()
  useEffect(() => {
    liveActiveChatIdRef.current = liveActiveChatId
  }, [liveActiveChatId])

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((toasts) => {
      const reminderToast = toasts.find((t) => t.id === 'reminder-toast')
      if (!reminderToast || reminderToast.exiting) {
        reminderBatchRef.current = []
      }
    })
    return () => unsubscribe()
  }, [])

  useGlobalEvent('reminder:auto-resolved', (reminder: ReminderApiItem) => {
    if (!isOnboardingComplete() || GlobalState.get<boolean>('sidepanel.quickTourActive', false)) return
    const id = `reminder-auto-resolved-${reminder.id}`
    toast.add({
      analytics: { type: 'reminder auto-resolved' },
      id,
      icon: <CircleCheck className="size-3.5 opacity-70" />,
      title: 'Checked off by Highlight',
      duration: 12000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 8000,
      keepExpandedOnRefresh: true,
      stableHeaderKey: id,
      priority: 1,
      onHeaderClick: () => {
        openViewInSidepanel('reminders', { initialTab: 'resolved', focusReminderId: reminder.id })
        toastManager.dismiss(id)
      },
      body: (toastId) => <AutoResolvedReminderToast reminder={reminder} toastId={toastId} />,
    })
  })

  useGlobalEvent('reminder:new', (reminder: ReminderApiItem) => {
    const onboardedInUserStore = isOnboardingComplete()
    const quickTourActive = GlobalState.get<boolean>('sidepanel.quickTourActive', false)

    // User-created reminders need no toast — they just made it. Only AI/agent
    // reminders (which carry a source) should notify.
    const isUserGenerated = !reminder.source

    // Already dealt with — dismissing removes it silently, so it must never
    // announce itself, whatever put it back in front of an emitter.
    const isDismissed = reminder.metadata?.dismissed === 'true'

    // Skip the toast when the reminder's spawning conversation is already
    // rendered inline in the active chat drawer AND the sidepanel is open.
    // Mirrors use-action-toasts.tsx (HIG-5126/HIG-5221). Check both the
    // (possibly stale) drawer context chatId and the live activeChatId atom to
    // cover the temp→real id transition window.
    const reminderConversationId = reminder.source?.type === 'conversation' ? reminder.source.id : undefined
    const sidepanelOpen = GlobalState.get<string>('sidepanel.state', 'hidden') === 'open'
    const topView = getSidepanelTopView()
    const isTopChatView = topView.id === 'chat'
    const contextChatId = viewContextOf(topView, 'chat')?.chatId
    const liveActiveChatId = isTopChatView ? liveActiveChatIdRef.current : undefined
    const inSpawningChat =
      !!reminderConversationId &&
      ((!!contextChatId && reminderConversationId === contextChatId) ||
        (!!liveActiveChatId && reminderConversationId === liveActiveChatId))

    if (
      !onboardedInUserStore ||
      quickTourActive ||
      isUserGenerated ||
      isDismissed ||
      (inSpawningChat && sidepanelOpen)
    ) {
      return
    }

    const isVisible = toastManager.getToasts().some((t) => t.id === 'reminder-toast' && !t.exiting)

    if (isVisible) {
      reminderBatchRef.current = [...reminderBatchRef.current, reminder]
    } else {
      reminderBatchRef.current = [reminder]
    }

    const reminders = reminderBatchRef.current
    const title = reminders.length === 1 ? '1 new reminder' : `${reminders.length} new reminders`
    const icon = <ListChecks className="size-3.5 opacity-70" />

    toast.add({
      analytics: { type: 'reminders', reminderCount: reminders.length },
      id: 'reminder-toast',
      icon,
      title: title.length > 40 ? title.slice(0, 40) + '…' : title,
      duration: 10000,
      autoExpandDelayMs: 0,
      autoCollapseDelayMs: 5000,
      keepExpandedOnRefresh: true,
      stableHeaderKey: 'reminder-toast',
      // Outrank informational toasts (e.g. chat-complete) so a burst of those
      // can't evict the actionable toast under the MAX_VISIBLE cap. HIG-5126.
      priority: 1,
      onHeaderClick: () => {
        openViewInSidepanel('reminders')
        toastManager.dismiss('reminder-toast')
      },
      body: (toastId) => <ReminderToast reminders={reminders} toastId={toastId} />,
    })

    if (!pendingReminderAnalyticsRef.current) {
      pendingReminderAnalyticsRef.current = true
      queueMicrotask(() => {
        pendingReminderAnalyticsRef.current = false
        const currentReminders = reminderBatchRef.current
        AmplitudeRenderer.trackEvent('Reminder', {
          action: 'toast shown',
          reminderIds: currentReminders.map((r) => r.id),
          count: currentReminders.length,
          types: currentReminders.map((r) => r.type),
        })
      })
    }
  })
}
